import React, { useState } from 'react';
import { 
  Wallet, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  Upload, 
  Camera, 
  Store, 
  Tag, 
  Gift, 
  Share2, 
  Copy, 
  TrendingUp, 
  AlertCircle, 
  Calendar, 
  ChevronRight, 
  Award, 
  Zap, 
  Sparkles,
  QrCode,
  ArrowUpRight,
  ArrowDownRight,
  Receipt,
  Search,
  Check,
  UserPlus,
  LogIn
} from 'lucide-react';
import { 
  CashbackCompany, 
  CashbackRequest, 
  CashbackTransaction, 
  CashbackCampaign, 
  CashbackMission, 
  CashbackReferral 
} from './CashbackTypes';

interface CashbackClientWalletProps {
  companies: CashbackCompany[];
  requests: CashbackRequest[];
  transactions: CashbackTransaction[];
  campaigns: CashbackCampaign[];
  missions: CashbackMission[];
  referral: CashbackReferral;
  onSubmitReceipt: (data: { business_id: string; amount: number; receipt_number: string; purchase_date: string; photo_url: string }) => void;
  onUseCashback: (business_id: string, amount: number) => void;
  currentUser?: any;
  onLoginRequest?: () => void;
}

export const CashbackClientWallet: React.FC<CashbackClientWalletProps> = ({
  companies,
  requests,
  transactions,
  campaigns,
  missions,
  referral,
  onSubmitReceipt,
  onUseCashback,
  currentUser,
  onLoginRequest
}) => {
  const [activeTab, setActiveTab] = useState<'wallet' | 'send_receipt' | 'history' | 'campaigns' | 'missions' | 'referral'>('wallet');
  
  // Auth alert modal state for guest visitors
  const [showAuthAlertModal, setShowAuthAlertModal] = useState(false);

  // Helper to guard actions
  const requireAuth = (action: () => void) => {
    if (!currentUser) {
      setShowAuthAlertModal(true);
    } else {
      action();
    }
  };
  
  // Form State for Sending Receipt
  const [selectedBusinessId, setSelectedBusinessId] = useState(companies[0]?.id || '');
  const [purchaseAmount, setPurchaseAmount] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split('T')[0]);
  const [receiptPhoto, setReceiptPhoto] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Redemption Modal State
  const [useModalOpen, setUseModalOpen] = useState(false);
  const [selectedRedeemCompany, setSelectedRedeemCompany] = useState<CashbackCompany | null>(null);
  const [redeemAmount, setRedeemAmount] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Calculations
  const availableBalance = transactions
    .filter(t => t.status === 'active')
    .reduce((acc, t) => acc + t.amount, 0);

  const pendingBalance = requests
    .filter(r => r.status === 'pending')
    .reduce((acc, r) => acc + r.calculated_cashback, 0);

  const usedBalance = Math.abs(
    transactions
      .filter(t => t.type === 'spend' || t.status === 'used')
      .reduce((acc, t) => acc + t.amount, 0)
  );

  const expiringSoonTransactions = transactions.filter(t => {
    if (t.status !== 'active' || !t.expires_at) return false;
    const expDate = new Date(t.expires_at);
    const today = new Date();
    const diffTime = expDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 15;
  });

  const expiringSoonTotal = expiringSoonTransactions.reduce((acc, t) => acc + t.amount, 0);

  const selectedCompany = companies.find(c => c.id === selectedBusinessId) || companies[0];

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      setShowAuthAlertModal(true);
      return;
    }
    if (!purchaseAmount || parseFloat(purchaseAmount) <= 0) {
      alert('Por favor, informe um valor válido para a compra.');
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      onSubmitReceipt({
        business_id: selectedBusinessId,
        amount: parseFloat(purchaseAmount),
        receipt_number: receiptNumber || `NF-${Math.floor(100000 + Math.random() * 900000)}`,
        purchase_date: purchaseDate,
        photo_url: receiptPhoto || 'https://images.unsplash.com/photo-1554415707-6e8cfc93fe23?w=600&h=800&fit=crop'
      });
      setIsSubmitting(false);
      setSubmitSuccess(true);
      setPurchaseAmount('');
      setReceiptNumber('');
      setReceiptPhoto(null);
      setTimeout(() => {
        setSubmitSuccess(false);
        setActiveTab('history');
      }, 1500);
    }, 800);
  };

  const handleCopyReferral = () => {
    const link = `${window.location.origin}/cashback?ref=${referral.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleShareWhatsapp = () => {
    const link = `${window.location.origin}/cashback?ref=${referral.referral_code}`;
    const text = encodeURIComponent(`👋 Olá! Use meu link para ganhar R$ ${referral.referred_bonus.toFixed(2)} de bônus na sua primeira compra com Cashback! Acesse: ${link}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  return (
    <div className="w-full space-y-6">
      {/* DEMO MODE NOTICE FOR GUESTS */}
      {!currentUser && (
        <div className="bg-gradient-to-r from-amber-500/10 via-indigo-500/10 to-emerald-500/10 border border-amber-500/30 rounded-3xl p-4 md:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-500/20 text-amber-700 rounded-2xl shrink-0">
              <Sparkles size={22} />
            </div>
            <div>
              <h4 className="text-xs md:text-sm font-black text-slate-900 uppercase tracking-wide flex items-center gap-2">
                Modo de Demonstração da Carteira
                <span className="bg-amber-100 text-amber-800 text-[10px] px-2.5 py-0.5 rounded-full font-bold">Exemplo Interativo</span>
              </h4>
              <p className="text-xs text-slate-600 mt-0.5">
                Você está visualizando um exemplo de como funciona a carteira. Cadastre-se ou faça login para enviar notas reais e acumular saldo!
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              if (onLoginRequest) onLoginRequest();
              else setShowAuthAlertModal(true);
            }}
            className="w-full sm:w-auto px-5 py-3 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-2xl shadow-md transition-all shrink-0 cursor-pointer text-center"
          >
            Cadastrar-se / Fazer Login
          </button>
        </div>
      )}

      {/* WALLET SUMMARY HEADER CARD */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-900 via-indigo-800 to-slate-900 p-6 md:p-8 text-white shadow-2xl border border-indigo-500/20">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-72 h-72 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/3 -mb-10 w-60 h-60 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs font-black uppercase tracking-widest rounded-full flex items-center gap-1.5">
                <Sparkles size={12} className="animate-spin" /> Carteira Digital Ativa
              </span>
              <span className="text-xs text-indigo-200 font-medium">
                {currentUser?.name ? `Olá, ${currentUser.name}` : 'Sua Carteira Multi-Empresa'}
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight text-white flex items-center gap-3">
              R$ {availableBalance.toFixed(2).replace('.', ',')}
            </h1>
            <p className="text-xs md:text-sm text-indigo-200 mt-1 font-medium">
              Saldo total disponível para usar em estabelecimentos parceiros
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => requireAuth(() => setActiveTab('send_receipt'))}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider px-5 py-3.5 rounded-2xl shadow-lg transition-all active:scale-95 cursor-pointer"
            >
              <Upload size={18} /> Enviar Nota Fiscal
            </button>
            <button
              onClick={() => requireAuth(() => setUseModalOpen(true))}
              className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-black text-xs uppercase tracking-wider px-5 py-3.5 rounded-2xl backdrop-blur-md border border-white/20 transition-all active:scale-95 cursor-pointer"
            >
              <QrCode size={18} /> Utilizar Cashback
            </button>
          </div>
        </div>

        {/* METRICS ROW */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4 mt-8 pt-6 border-t border-white/10">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-3.5 border border-white/5">
            <div className="flex items-center gap-1.5 text-indigo-200 text-[10px] font-bold uppercase tracking-wider mb-1">
              <Clock size={12} className="text-amber-400" /> Pendente
            </div>
            <div className="text-base md:text-lg font-black text-amber-300">
              R$ {pendingBalance.toFixed(2).replace('.', ',')}
            </div>
            <p className="text-[10px] text-indigo-300/80 mt-0.5">Em análise pelas lojas</p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-3.5 border border-white/5">
            <div className="flex items-center gap-1.5 text-indigo-200 text-[10px] font-bold uppercase tracking-wider mb-1">
              <CheckCircle2 size={12} className="text-emerald-400" /> Resgatado
            </div>
            <div className="text-base md:text-lg font-black text-emerald-300">
              R$ {usedBalance.toFixed(2).replace('.', ',')}
            </div>
            <p className="text-[10px] text-indigo-300/80 mt-0.5">Total já economizado</p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-3.5 border border-white/5">
            <div className="flex items-center gap-1.5 text-indigo-200 text-[10px] font-bold uppercase tracking-wider mb-1">
              <AlertCircle size={12} className="text-rose-400" /> Preste a Expirar
            </div>
            <div className="text-base md:text-lg font-black text-rose-300">
              R$ {expiringSoonTotal.toFixed(2).replace('.', ',')}
            </div>
            <p className="text-[10px] text-indigo-300/80 mt-0.5">Próximos 15 dias</p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-3.5 border border-white/5">
            <div className="flex items-center gap-1.5 text-indigo-200 text-[10px] font-bold uppercase tracking-wider mb-1">
              <Gift size={12} className="text-indigo-400" /> Bônus & Indicação
            </div>
            <div className="text-base md:text-lg font-black text-indigo-200">
              R$ {referral.total_earned.toFixed(2).replace('.', ',')}
            </div>
            <p className="text-[10px] text-indigo-300/80 mt-0.5">{referral.total_referred} amigos indicados</p>
          </div>
        </div>
      </div>

      {/* WARNING BANNER IF EXPIRING SOON */}
      {expiringSoonTotal > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center justify-between gap-4 animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500 text-white rounded-xl shrink-0">
              <AlertCircle size={20} />
            </div>
            <div>
              <h4 className="text-xs font-black text-amber-900 uppercase tracking-wide">
                Você tem R$ {expiringSoonTotal.toFixed(2)} prestes a expirar!
              </h4>
              <p className="text-xs text-amber-700 mt-0.5">
                Utilize seu saldo em compras nas lojas parceiras antes do vencimento para não perder.
              </p>
            </div>
          </div>
          <button
            onClick={() => requireAuth(() => setUseModalOpen(true))}
            className="hidden sm:inline-flex px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl transition-colors shrink-0"
          >
            Usar Agora
          </button>
        </div>
      )}

      {/* NAVIGATION TABS */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-slate-200 no-scrollbar">
        {[
          { id: 'wallet', label: 'Início & Resumo', icon: Wallet },
          { id: 'send_receipt', label: 'Enviar Nota Fiscal', icon: Camera },
          { id: 'history', label: 'Histórico & Extrato', icon: Receipt },
          { id: 'campaigns', label: 'Campanhas Promocionais', icon: Tag },
          { id: 'missions', label: 'Missões & Desafios', icon: Award },
          { id: 'referral', label: 'Indique e Ganhe', icon: Gift }
        ].map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-xs font-bold uppercase tracking-wider transition-all whitespace-nowrap shrink-0 cursor-pointer ${
                isActive
                  ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* TAB 1: WALLET OVERVIEW */}
      {activeTab === 'wallet' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT 2 COLS: RECENT TRANSACTIONS & PARTICIPATING STORES */}
          <div className="lg:col-span-2 space-y-6">
            {/* STORES WITH CASHBACK */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                    <Store className="text-indigo-600" size={18} /> Lojas Participantes com Cashback
                  </h3>
                  <p className="text-xs text-slate-500">
                    Onde você pode acumular ou utilizar seu saldo
                  </p>
                </div>
                <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full">
                  {companies.length} empresas
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {companies.map(company => (
                  <div
                    key={company.id}
                    className="p-4 rounded-2xl border border-slate-100 bg-slate-50/50 hover:border-indigo-200 hover:bg-indigo-50/30 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={company.logo_url}
                        alt={company.name}
                        className="w-12 h-12 rounded-xl object-cover border border-slate-200 shadow-sm shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-black text-slate-800 truncate group-hover:text-indigo-600 transition-colors">
                          {company.name}
                        </h4>
                        <span className="text-[10px] font-bold text-slate-400 uppercase block">
                          {company.category}
                        </span>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                            {company.settings.default_percent}% Cashback
                          </span>
                          {company.cross_cashback_enabled && (
                            <span className="text-[9px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-md">
                              Cruzado
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PENDING NOTA FISCAL REQUESTS */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm">
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide mb-4 flex items-center gap-2">
                <Clock className="text-amber-500" size={18} /> Solicitações Recentes de Nota Fiscal
              </h3>

              {requests.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <Receipt className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p className="text-xs font-bold">Nenhuma nota fiscal enviada ainda.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {requests.slice(0, 4).map(req => (
                    <div
                      key={req.id}
                      className="p-4 rounded-2xl border border-slate-100 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={req.receipt_photo}
                          alt="Comprovante"
                          className="w-12 h-12 rounded-xl object-cover border border-slate-200 shrink-0"
                        />
                        <div>
                          <h4 className="text-xs font-black text-slate-800">{req.business_name}</h4>
                          <span className="text-[10px] text-slate-400 block font-mono">
                            {req.receipt_number} • {req.purchase_date}
                          </span>
                          <span className="text-xs font-bold text-slate-700">
                            Compra R$ {req.purchase_amount.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <div className="text-right shrink-0">
                        <span className="text-sm font-black text-emerald-600 block">
                          + R$ {req.calculated_cashback.toFixed(2)}
                        </span>
                        {req.status === 'pending' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
                            <Clock size={10} /> Em Análise
                          </span>
                        )}
                        {req.status === 'approved' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                            <CheckCircle2 size={10} /> Aprovada
                          </span>
                        )}
                        {req.status === 'rejected' && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">
                            <XCircle size={10} /> Rejeitada
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT COL: QUICK MISSIONS & REFERRAL PROMO */}
          <div className="space-y-6">
            {/* MISSIONS CARD */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                  <Award className="text-indigo-600" size={18} /> Desafios & Missões
                </h3>
                <button
                  onClick={() => setActiveTab('missions')}
                  className="text-xs font-bold text-indigo-600 hover:underline"
                >
                  Ver Todas
                </button>
              </div>

              <div className="space-y-4">
                {missions.slice(0, 3).map(mis => {
                  const percent = Math.min(100, Math.round((mis.current_count / mis.target_count) * 100));
                  return (
                    <div key={mis.id} className="p-3.5 rounded-2xl bg-slate-50 border border-slate-100 space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-black text-slate-800">{mis.title}</h4>
                        <span className="text-xs font-black text-emerald-600 bg-emerald-100/60 px-2 py-0.5 rounded-lg">
                          +R$ {mis.reward_cashback.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500">{mis.description}</p>
                      
                      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-indigo-600 h-full rounded-full transition-all duration-500"
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-slate-400 font-bold">
                        <span>{mis.current_count} de {mis.target_count} concluído</span>
                        <span>{percent}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* REFERRAL CALLOUT CARD */}
            <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-3xl p-6 text-white shadow-xl space-y-4 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4 opacity-10">
                <Gift size={100} />
              </div>
              <span className="px-2.5 py-1 bg-white/20 text-white text-[10px] font-black uppercase tracking-widest rounded-full">
                Indique e Ganhe
              </span>
              <h3 className="text-lg font-black tracking-tight leading-snug">
                Ganhe R$ {referral.referrer_bonus.toFixed(2)} por cada amigo indicado!
              </h3>
              <p className="text-xs text-indigo-100">
                Seu amigo também ganha R$ {referral.referred_bonus.toFixed(2)} na primeira compra dele em qualquer empresa cadastrada.
              </p>

              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-3 flex items-center justify-between gap-2 border border-white/20">
                <span className="font-mono text-xs font-bold tracking-wider text-amber-300 truncate">
                  {referral.referral_code}
                </span>
                <button
                  onClick={() => requireAuth(handleCopyReferral)}
                  className="px-3 py-1.5 bg-white text-indigo-900 font-bold text-xs uppercase rounded-xl hover:bg-amber-300 transition-colors shrink-0"
                >
                  {copySuccess ? 'Copiado!' : 'Copiar Link'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: SEND RECEIPT FORM */}
      {activeTab === 'send_receipt' && (
        <div className="max-w-2xl mx-auto bg-white rounded-3xl p-6 md:p-8 border border-slate-200/80 shadow-md">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <Camera size={28} />
            </div>
            <h2 className="text-xl font-black text-slate-800 uppercase tracking-wide">
              Cadastrar Nota Fiscal / Comprovante
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Envie a foto do seu cupom fiscal para receber seu cashback creditado automaticamente.
            </p>
          </div>

          {submitSuccess ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center animate-fade-in">
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-lg font-black text-emerald-900">Nota Fiscal Enviada com Sucesso!</h3>
              <p className="text-xs text-emerald-700 mt-1">
                Sua solicitação está em análise pelo estabelecimento. O saldo de cashback será liberado assim que for aprovado.
              </p>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="space-y-5">
              {/* SELECT BUSINESS */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  1. Selecione o Estabelecimento *
                </label>
                <select
                  value={selectedBusinessId}
                  onChange={(e) => setSelectedBusinessId(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 focus:outline-none focus:border-indigo-600 transition-colors"
                >
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name} ({company.settings.default_percent}% Cashback)
                    </option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  Regra da loja: {selectedCompany.settings.default_percent}% de cashback para compras acima de R$ {selectedCompany.settings.min_purchase.toFixed(2)}
                </p>
              </div>

              {/* INPUT VALUES */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                    2. Valor Total da Compra (R$) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Ex: 150.00"
                    required
                    value={purchaseAmount}
                    onChange={(e) => setPurchaseAmount(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 focus:outline-none focus:border-indigo-600 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                    3. Data da Compra *
                  </label>
                  <input
                    type="date"
                    required
                    value={purchaseDate}
                    onChange={(e) => setPurchaseDate(e.target.value)}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 focus:outline-none focus:border-indigo-600 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  4. Número do Cupom / Nota Fiscal (Opcional)
                </label>
                <input
                  type="text"
                  placeholder="Ex: NF-99201"
                  value={receiptNumber}
                  onChange={(e) => setReceiptNumber(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800 focus:outline-none focus:border-indigo-600 transition-colors"
                />
              </div>

              {/* UPLOAD PHOTO */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
                  5. Foto da Nota Fiscal / Cupom *
                </label>
                <div className="border-2 border-dashed border-slate-300 hover:border-indigo-500 rounded-2xl p-6 text-center transition-colors bg-slate-50 cursor-pointer relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                  />
                  {receiptPhoto ? (
                    <div className="space-y-2">
                      <img
                        src={receiptPhoto}
                        alt="Preview da Nota"
                        className="max-h-48 mx-auto rounded-xl object-contain border border-slate-200 shadow-md"
                      />
                      <p className="text-xs font-bold text-emerald-600 flex items-center justify-center gap-1">
                        <CheckCircle2 size={14} /> Foto Selecionada! Clique para alterar.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Camera className="w-10 h-10 text-slate-400 mx-auto" />
                      <p className="text-xs font-bold text-slate-700">Tirar Foto ou Selecionar Arquivo</p>
                      <p className="text-[11px] text-slate-400">Garanta que o valor e a data estejam legíveis</p>
                    </div>
                  )}
                </div>
              </div>

              {/* CALCULATED CASHBACK PREVIEW */}
              {purchaseAmount && parseFloat(purchaseAmount) > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center justify-between">
                  <span className="text-xs font-bold text-emerald-800 uppercase tracking-wide">
                    Cashback Estimado:
                  </span>
                  <span className="text-lg font-black text-emerald-600">
                    + R$ {(parseFloat(purchaseAmount) * (selectedCompany.settings.default_percent / 100)).toFixed(2)}
                  </span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg transition-all active:scale-95 cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? 'Processando Envio...' : 'Enviar Comprovante para Análise'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* TAB 3: HISTORY & AUDIT EXTRATO */}
      {activeTab === 'history' && (
        <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide">
                Extrato Financeiro & Histórico
              </h3>
              <p className="text-xs text-slate-500">
                Registro completo de todas as movimentações para auditoria
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <th className="py-3 px-4">Data</th>
                  <th className="py-3 px-4">Estabelecimento</th>
                  <th className="py-3 px-4">Descrição</th>
                  <th className="py-3 px-4">Tipo</th>
                  <th className="py-3 px-4 text-right">Valor (R$)</th>
                  <th className="py-3 px-4 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {transactions.map(tx => (
                  <tr key={tx.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3.5 px-4 font-mono text-slate-500">{tx.date}</td>
                    <td className="py-3.5 px-4 font-bold text-slate-800">{tx.business_name}</td>
                    <td className="py-3.5 px-4 text-slate-600">{tx.description}</td>
                    <td className="py-3.5 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                        tx.type === 'earn' ? 'bg-emerald-100 text-emerald-800' :
                        tx.type === 'spend' ? 'bg-indigo-100 text-indigo-800' :
                        tx.type === 'bonus' ? 'bg-purple-100 text-purple-800' : 'bg-rose-100 text-rose-800'
                      }`}>
                        {tx.type}
                      </span>
                    </td>
                    <td className={`py-3.5 px-4 font-black text-right ${tx.amount > 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
                      {tx.amount > 0 ? `+ R$ ${tx.amount.toFixed(2)}` : `- R$ ${Math.abs(tx.amount).toFixed(2)}`}
                    </td>
                    <td className="py-3.5 px-4 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        tx.status === 'active' ? 'bg-emerald-50 text-emerald-600' :
                        tx.status === 'used' ? 'bg-slate-100 text-slate-500' : 'bg-rose-50 text-rose-600'
                      }`}>
                        {tx.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 4: CAMPAIGNS */}
      {activeTab === 'campaigns' && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm">
            <h3 className="text-base font-black text-slate-800 uppercase tracking-wide mb-2 flex items-center gap-2">
              <Tag className="text-indigo-600" size={20} /> Campanhas Promocionais Ativas
            </h3>
            <p className="text-xs text-slate-500">
              Aproveite taxas turbinadas de cashback em datas e momentos especiais
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map(camp => (
              <div key={camp.id} className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-md flex flex-col justify-between space-y-4 hover:border-indigo-300 transition-all">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-full">
                      {camp.business_name}
                    </span>
                    <span className="text-xs font-black text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                      {camp.cashback_percent}% Cashback
                    </span>
                  </div>

                  <h4 className="text-base font-black text-slate-800 leading-snug">{camp.title}</h4>
                  <p className="text-xs text-slate-600 mt-2">{camp.description}</p>
                </div>

                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400 font-medium">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} /> Validade: {camp.end_date}
                  </span>
                  <button
                    onClick={() => requireAuth(() => setActiveTab('send_receipt'))}
                    className="text-indigo-600 font-bold hover:underline cursor-pointer"
                  >
                    Participar →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TAB 5: MISSIONS */}
      {activeTab === 'missions' && (
        <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm space-y-6">
          <div>
            <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
              <Award className="text-amber-500" size={20} /> Missões e Desafios com Recompensa
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Conclua tarefas no ecossistema e receba dinheiro extra na sua carteira.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {missions.map(mis => {
              const percent = Math.min(100, Math.round((mis.current_count / mis.target_count) * 100));
              return (
                <div key={mis.id} className="p-5 rounded-2xl bg-slate-50 border border-slate-200 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-black text-slate-800">{mis.title}</h4>
                    <span className="text-xs font-black text-emerald-600 bg-emerald-100 px-3 py-1 rounded-xl">
                      + R$ {mis.reward_cashback.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600">{mis.description}</p>

                  <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-indigo-600 h-full rounded-full transition-all duration-500"
                      style={{ width: `${percent}%` }}
                    ></div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-slate-500 font-bold">
                    <span>Progresso: {mis.current_count} / {mis.target_count}</span>
                    <span>{percent}% Concluído</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* TAB 6: REFERRAL PROGRAM */}
      {activeTab === 'referral' && (
        <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-200/80 shadow-sm max-w-3xl mx-auto space-y-6">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-3xl flex items-center justify-center mx-auto">
              <Gift size={32} />
            </div>
            <h2 className="text-2xl font-black text-slate-800 tracking-tight uppercase">
              Programa Indique e Ganhe
            </h2>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Indique amigos para usarem o sistema de cashback. Você ganha R$ {referral.referrer_bonus.toFixed(2)} e seu amigo ganha R$ {referral.referred_bonus.toFixed(2)} na primeira compra!
            </p>
          </div>

          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
            <label className="block text-xs font-bold uppercase text-slate-700">Seu Link Exclusivo de Indicação</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={`${window.location.origin}/cashback?ref=${referral.referral_code}`}
                className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono text-slate-700"
              />
              <button
                onClick={() => requireAuth(handleCopyReferral)}
                className="px-4 py-3 bg-indigo-600 text-white font-bold text-xs uppercase rounded-xl hover:bg-indigo-700 transition-colors shrink-0 cursor-pointer"
              >
                {copySuccess ? 'Copiado!' : 'Copiar'}
              </button>
            </div>

            <button
              onClick={() => requireAuth(handleShareWhatsapp)}
              className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-colors flex items-center justify-center gap-2 shadow-md cursor-pointer"
            >
              <Share2 size={16} /> Compartilhar no WhatsApp
            </button>
          </div>
        </div>
      )}

      {/* REDEEM CASHBACK MODAL */}
      {useModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-slate-100 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h3 className="text-base font-black text-slate-800 uppercase tracking-wide flex items-center gap-2">
                <QrCode className="text-indigo-600" size={20} /> Utilizar Saldo de Cashback
              </h3>
              <button
                onClick={() => setUseModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-slate-700 mb-2">
                Selecione o Estabelecimento
              </label>
              <select
                value={selectedRedeemCompany?.id || companies[0]?.id}
                onChange={(e) => setSelectedRedeemCompany(companies.find(c => c.id === e.target.value) || companies[0])}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-bold text-slate-800"
              >
                {companies.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.cross_cashback_enabled ? '(Aceita Cashback Cruzado)' : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase text-slate-700 mb-2">
                Valor do Resgate (R$) (Máx R$ {availableBalance.toFixed(2)})
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="Ex: 20.00"
                value={redeemAmount}
                onChange={(e) => setRedeemAmount(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-800"
              />
            </div>

            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4 text-center">
              <p className="text-xs text-indigo-800 font-bold">
                Apresente o código abaixo ao caixa para aplicar o desconto de R$ {parseFloat(redeemAmount || '0').toFixed(2)}
              </p>
              <div className="my-3 font-mono text-2xl font-black text-indigo-600 tracking-widest bg-white py-2 rounded-xl border border-indigo-200 shadow-inner">
                CASH-{Math.floor(100000 + Math.random() * 900000)}
              </div>
            </div>

            <button
              onClick={() => {
                if (!currentUser) {
                  setShowAuthAlertModal(true);
                  return;
                }
                if (!redeemAmount || parseFloat(redeemAmount) <= 0) return alert('Informe um valor válido.');
                onUseCashback(selectedRedeemCompany?.id || companies[0].id, parseFloat(redeemAmount));
                setUseModalOpen(false);
                setRedeemAmount('');
                alert('Resgate gerado com sucesso! Apresente o código no caixa.');
              }}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg transition-all cursor-pointer"
            >
              Confirmar Resgate no Caixa
            </button>
          </div>
        </div>
      )}

      {/* AUTH REQUIRED ALERT MODAL FOR GUESTS */}
      {showAuthAlertModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-slate-100 text-center space-y-5 relative">
            <button 
              onClick={() => setShowAuthAlertModal(false)} 
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 font-bold p-2 cursor-pointer text-lg"
            >
              ✕
            </button>

            <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto shadow-inner">
              <UserPlus size={32} />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-slate-900 tracking-tight">
                Cadastro de Usuário Necessário
              </h3>
              <p className="text-xs text-slate-600 leading-relaxed font-medium">
                Para enviar notas fiscais, acumular saldo em dinheiro, resgatar cupons e utilizar seu cashback nas empresas parceiras, você precisa criar uma conta ou fazer login no Automatiza e Vende.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-4 text-left space-y-2 text-xs">
              <p className="font-bold text-slate-800 uppercase text-[10px] tracking-wider mb-1">
                Ao criar sua conta gratuita você tem:
              </p>
              <div className="flex items-start gap-2 text-slate-700 font-medium">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <span>Envio rápido de fotos de cupons e notas fiscais</span>
              </div>
              <div className="flex items-start gap-2 text-slate-700 font-medium">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <span>Saldo unificado e cashback cruzado entre lojas</span>
              </div>
              <div className="flex items-start gap-2 text-slate-700 font-medium">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <span>Resgate de descontos direto no caixa dos parceiros</span>
              </div>
              <div className="flex items-start gap-2 text-slate-700 font-medium">
                <CheckCircle2 size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                <span>Bônus por indicar amigos no programa</span>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <button
                onClick={() => {
                  setShowAuthAlertModal(false);
                  if (onLoginRequest) {
                    onLoginRequest();
                  } else {
                    alert('Por favor, utilize o botão de "Entrar / Cadastrar" no topo da página.');
                  }
                }}
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs uppercase tracking-wider rounded-2xl shadow-lg transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-2"
              >
                <LogIn size={16} /> Cadastrar-se / Entrar no Automatiza e Vende
              </button>

              <button
                onClick={() => setShowAuthAlertModal(false)}
                className="w-full py-2.5 text-slate-500 hover:text-slate-800 font-bold text-xs transition-colors cursor-pointer"
              >
                Continuar Navegando em Modo Exemplo
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
